<template>
  <el-container class="chat-page" :class="{ 'h5-detail-open': !!activeChat }">
    <el-aside width="260px" class="chat-list-box">
      <div class="chat-list-header">
        <el-input class="search-text" size="small" placeholder="搜索" v-model="searchText" clearable>
          <i class="el-icon-search el-input__icon" slot="prefix"> </i>
        </el-input>
        <span class="read-all-btn" :class="{ disabled: unreadCount <= 0 || readingAll }"
          title="一键已读" @click="onReadAll">
          <i class="el-icon-circle-check"></i>
        </span>
      </div>
      <div class="chat-list-loading" v-if="showSyncLoading" v-loading="true" element-loading-text="正在从云端同步消息..."
        element-loading-spinner="el-icon-loading" element-loading-background="#F9F9F9" element-loading-size="24">
      </div>
      <virtual-scroller
        v-if="visibleChats.length && !showSyncLoading"
        class="chat-list-items"
        :items="visibleChats"
        :item-height="60"
        :item-key="chatItemKey"
      >
        <template v-slot="{ item }">
          <chat-item
            :chat="item"
            @click.native="onActiveItem(item)"
            @delete="onDelItem(item)"
            @top="onTop(item)"
            @canceltop="onCancelTop(item)"
            @nohint="onNoHint(item, true)"
            @cancelnohint="onNoHint(item, false)"
            :active="isActiveChat(item)"
            :pined="!!item.pinTime"
            :refresh-tick="timeRefreshTick"
            @info="onShowInfo(item)"
          ></chat-item>
        </template>
      </virtual-scroller>
      <!-- <el-empty description="聊天记录为空" v-if="chatStore.chats.length === 0 && !loading"></el-empty> -->
    </el-aside>
    <el-container class="chat-box">
      <div v-if="mediaDownloadCount > 0" class="media-download-bar">
        <i class="el-icon-loading"></i>
        <span class="media-download-bar__text">{{ mediaDownloadText }}</span>
      </div>
      <div class="chat-box-body">
        <chat-box v-if="activeChat && activeChat?.type != 'SYSTEM'" :chat="activeChat" :searchHistory="searchHistory"></chat-box>
        <chat-system-box v-if="activeChat && activeChat.type == 'SYSTEM'" :chat="activeChat"></chat-system-box>
      </div>
    </el-container>
  </el-container>
</template>

<script>
import http from '@/api/httpRequest.js';
import ChatBox from "../components/chat/ChatBox.vue";
import ChatItem from "../components/chat/ChatItem.vue";
import ChatSystemBox from "../components/chat/ChatSystemBox.vue";
import VirtualScroller from "../components/common/VirtualScroller.vue";
import { chatMatchesKeyword, chatMetaMatchesKeyword, chatSearchKey } from '@/utils/chatSearch.js';

export default {
  name: "chat",
  components: {
    ChatItem,
    ChatBox,
    ChatSystemBox,
    VirtualScroller
  },
  data() {
    return {
      searchText: "",
      messageContent: "",
      group: {},
      groupMembers: [],
      lastActiveTime: 0, // 记录上次点击时间
      searchHistory: {
        searchText: '',
        show: false,
      },
      historyMatchKeys: [],
      searchSeq: 0,
      searchTimer: null,
      mediaDownloadCount: 0,
      mediaDownloadLabel: '',
      timeRefreshTick: Date.now(),
      timeRefreshTimer: null,
      readingAll: false,
    }
  },
  mounted() {
    this.startTimeRefresh();
    this.$eventBus.$on('media-download:start', this.onMediaDownloadStart);
    this.$eventBus.$on('media-download:end', this.onMediaDownloadEnd);
    if (this.activeChat) {
      this.$store.dispatch("hydrateChatMessages", this.activeChat);
    }
    this.$nextTick(() => {
      if (!this.loading && this.chatStore.chats.length) {
        this.fetchGroupNotDisturbForChats();
      }
    });
  },
  beforeDestroy() {
    this.stopTimeRefresh();
    this.clearHistorySearch();
    this.$eventBus.$off('media-download:start', this.onMediaDownloadStart);
    this.$eventBus.$off('media-download:end', this.onMediaDownloadEnd);
  },
  watch: {
    loading(val, oldVal) {
      if (oldVal === true && val === false) {
        this.$nextTick(() => this.fetchGroupNotDisturbForChats());
        this.scheduleHistorySearch();
      }
    },
    searchText() {
      this.scheduleHistorySearch();
    },
  },
  methods: {
    startTimeRefresh() {
      this.stopTimeRefresh();
      this.timeRefreshTimer = setInterval(() => {
        this.timeRefreshTick = Date.now();
      }, 5 * 60 * 1000);
    },
    stopTimeRefresh() {
      if (this.timeRefreshTimer) {
        clearInterval(this.timeRefreshTimer);
        this.timeRefreshTimer = null;
      }
    },
    clearHistorySearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
      this.searchSeq += 1;
      this.historyMatchKeys = [];
    },
    scheduleHistorySearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
      const keyword = (this.searchText || '').trim();
      this.searchSeq += 1;
      this.historyMatchKeys = [];
      if (!keyword) {
        return;
      }
      const seq = this.searchSeq;
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null;
        this.runHistorySearch(keyword, seq);
      }, 280);
    },
    runHistorySearch(keyword, seq) {
      this.$store.dispatch('findChatsMatchingKeyword', {
        keyword,
        isCancelled: () => seq !== this.searchSeq
      }).then((keys) => {
        if (seq !== this.searchSeq || (this.searchText || '').trim() !== keyword) {
          return;
        }
        this.historyMatchKeys = Array.isArray(keys) ? keys : [];
      }).catch(() => {
        if (seq === this.searchSeq) {
          this.historyMatchKeys = [];
        }
      });
    },
    onMediaDownloadStart(payload) {
      this.mediaDownloadCount += 1;
      if (payload && payload.fileName) {
        const sizePart = payload.sizeText ? `（${payload.sizeText}）` : '';
        this.mediaDownloadLabel = `「${payload.fileName}」${sizePart}`;
      }
    },
    onMediaDownloadEnd() {
      this.mediaDownloadCount = Math.max(0, this.mediaDownloadCount - 1);
      if (this.mediaDownloadCount === 0) {
        this.mediaDownloadLabel = '';
      }
    },
    /** 会话列表已展示后，拉取群资料中的免打扰状态写回会话 */
    fetchGroupNotDisturbForChats() {
      if (this.loading || !this.chatStore.chats.length) {
        return;
      }
      const ids = [
        ...new Set(
          this.chatStore.chats
            .filter((c) => c.type === "GROUP" && !c.delete)
            .map((c) => c.targetId)
        ),
      ];
      if (!ids.length) {
        return;
      }
      // 大账号全量拉群资料会打爆浏览器，仅补齐当前可见会话
      const visibleIds = ids.slice(0, 30);
      visibleIds.forEach((id) => {
        http({
          url: `/group/find/${id}`,
          method: "get",
        })
          .then((group) => {
            if (group && group.id != null) {
              this.$store.commit("updateChatFromGroup", group);
            }
          })
          .catch(() => {});
      });
    },
    isListableChat(chat) {
      if (!chat || chat.delete) {
        return false;
      }
      if (chat.type != 'PRIVATE') {
        return true;
      }
      if (this.$store.getters.isHiddenPrivateChat(chat.targetId)) {
        return false;
      }
      const friendsReady = this.$store.state.friendStore && this.$store.state.friendStore.friendsVersion > 0;
      if (friendsReady && !this.$store.getters.isFriend(chat.targetId)) {
        return false;
      }
      return true;
    },
    chatItemKey(chat) {
      return chat.type + '-' + chat.targetId;
    },
    isActiveChat(chat) {
      const active = this.chatStore && this.chatStore.activeChat;
      return !!(active && chat && active.type == chat.type && active.targetId == chat.targetId);
    },
    findChatIndex(chat) {
      const chats = this.displayChats;
      if (!chat || !Array.isArray(chats)) {
        return -1;
      }
      return chats.findIndex((item) => item.type == chat.type && item.targetId == chat.targetId);
    },
    onActiveItem(chat) {
      const keyword = (this.searchText || '').trim();
      if (keyword && !chatMetaMatchesKeyword(chat, keyword.toLowerCase())) {
        this.onActiveHistoryItem(chat);
        return;
      }
      this.searchHistory = {
        searchText: '',
        show: false,
      }
      const now = Date.now();
      if (now - this.lastActiveTime < 100) {
        return;
      }
      this.lastActiveTime = now;
      if (this.isActiveChat(chat)) {
        return;
      }
      const index = this.findChatIndex(chat);
      if (index < 0) {
        return;
      }
      this.$store.dispatch("activateChat", -1);
      setTimeout(() => {
        this.$store.dispatch("activateChat", index);
      }, 10);
    },
    onActiveHistoryItem(chat) {
      this.searchHistory = {
        searchText: (this.searchText || '').trim(),
        show: true,
      }
      const now = Date.now();
      if (now - this.lastActiveTime < 100) {
        return;
      }
      this.lastActiveTime = now;
      const index = this.findChatIndex(chat);
      if (index < 0) {
        return;
      }
      this.$store.dispatch("activateChat", -1);
      setTimeout(() => {
        this.$store.dispatch("activateChat", index);
      }, 10);
    },
    onDelItem(chat) {
      const index = this.findChatIndex(chat);
      if (index < 0) {
        return;
      }
      this.$store.commit("removeChat", index);
    },
    onShowInfo(chat) {
      if (chat.type == 'PRIVATE') {
        this.$router.push("/home/friend?id=" + chat.targetId);
      } else if (chat.type == 'GROUP') {
        if (!this.$store.getters.isGroup(chat.targetId)) {
          this.$message.error("您已不在群聊中，无法查看群资料")
          return;
        }
        this.$router.push("/home/group?id=" + chat.targetId);
      }
    },
    onTop(chat) {
      const chatIdx = this.findChatIndex(chat);
      if (chatIdx < 0) {
        return;
      }
      if (this.chatStore.chats[chatIdx].type == 'SYSTEM') {
          this.$message.error("系统消息无法置顶")
          return;
      }
      http({
        url: `/user/chat/pinChat`,
        method: 'put',
        data: {
          pinType: this.chatStore.chats[chatIdx].type == 'PRIVATE' ? 1 : 2,
          pinId: this.chatStore.chats[chatIdx].targetId
        }
      }).then(res => {
        const list = [
          ...this.chatStore.pinedChats,
          {
            pinId: this.chatStore.chats[chatIdx].targetId,
            pinType: this.chatStore.chats[chatIdx].type == 'PRIVATE' ? 1 : 2,
            pinTime: new Date().getTime(),
          }
        ]
        this.$store.commit("setPinedChats", list);
        this.$store.commit("moveTop", chatIdx);
        this.$message.success("置顶成功")
      })
    },
    onCancelTop(chat) {
      const chatIdx = this.findChatIndex(chat);
      if (chatIdx < 0) {
        return;
      }
      const chatItem = this.chatStore.chats[chatIdx];
      http({
        url: `/user/chat/unPinChat`,
        method: 'put',
        data: {
          pinType: chatItem.type == 'PRIVATE' ? 1 : 2,
          pinId: chatItem.targetId,
        }
      }).then(res => {
        const _list = ['', 'PRIVATE', 'GROUP'];
        const list = this.chatStore.pinedChats.filter(item => {
          return !(chatItem.targetId == item.pinId && chatItem.type == _list[item.pinType])
        })
        this.$store.commit("setPinedChats", list);
        this.$store.commit("cancelTop", chatIdx);
        this.$message.success("已取消置顶")
      })
    },
    onReadAll() {
      if (this.readingAll || this.unreadCount <= 0) {
        return;
      }
      this.readingAll = true;
      http({
        url: '/message/readed/all',
        method: 'put'
      }).then(() => {
        this.$store.commit('resetAllUnreadCount');
        this.$message.success('已全部标为已读');
      }).catch((e) => {
        this.$message.error((e && e.message) || '一键已读失败');
      }).finally(() => {
        this.readingAll = false;
      });
    },
    onNoHint(chat, isNoHint) {
      const chatIdx = this.findChatIndex(chat);
      if (chatIdx < 0) {
        return;
      }
      const chatItem = this.chatStore.chats[chatIdx];
      http({
        url: `/group/setGroupNotDisturb`,
        method: 'put',
        data: {
          "groupId": chatItem.targetId,
          "isNotDisturb": isNoHint
        }
      }).then(res => {
        let obj = {
          type: isNoHint ? 'noHint' : 'cancelNoHint',
          msg: isNoHint ? '该群开启免打扰' : '该群取消免打扰'
        }
        this.$store.commit(obj.type, chatIdx);
        this.$message.success(obj.msg)
      })
    },
  },
  computed: {
    mediaDownloadText() {
      if (this.mediaDownloadCount > 1) {
        return `正在下载 ${this.mediaDownloadCount} 个文件，请稍候...`;
      }
      if (this.mediaDownloadLabel) {
        return `正在下载 ${this.mediaDownloadLabel}，请稍候...`;
      }
      return '正在下载，请稍候...';
    },
    chatStore() {
      return this.$store.state.chatStore;
    },
    activeChat() {
      return this.chatStore.activeChat;
    },
    loading() {
      return this.chatStore.loading;
    },
    // 唤醒/断线重连时不盖全屏 loading，保持会话列表可见
    showSyncLoading() {
      return this.chatStore.loading && !this.chatStore.isReconnecting;
    },
    displayChats() {
      if (this.chatStore.isReconnecting && this.chatStore.loading) {
        return this.$store.getters.findChats() || [];
      }
      return this.chatStore.chats || [];
    },
    unreadCount() {
      return (this.displayChats || []).reduce((sum, chat) => {
        if (!chat || chat.delete) {
          return sum;
        }
        return sum + (chat.unreadCount || 0);
      }, 0);
    },
    visibleChats() {
      const chats = (this.displayChats || []).filter((chat) => this.isListableChat(chat));
      const keyword = (this.searchText || '').trim();
      if (!keyword) {
        return chats;
      }
      const lower = keyword.toLowerCase();
      const extraKeys = this.historyMatchKeys || [];
      return chats.filter((chat) => {
        if (chatMatchesKeyword(chat, lower)) {
          return true;
        }
        return extraKeys.indexOf(chatSearchKey(chat)) >= 0;
      });
    },
  }
}
</script>

<style lang="scss">
.chat-page {
  height: 100%;
  width: 100%;

  .chat-box {
    flex-direction: column;
    overflow: hidden;

    .media-download-bar {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      gap: 8px;
      height: 36px;
      padding: 0 16px;
      background: #ecf5ff;
      border-bottom: 1px solid #d9ecff;
      color: var(--im-color-primary, #092f80);
      font-size: 13px;
      z-index: 10;

      .el-icon-loading {
        font-size: 16px;
      }

      &__text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .chat-box-body {
      flex: 1;
      min-height: 0;
      position: relative;
      overflow: hidden;
    }
  }

  .chat-list-box {
    display: flex;
    flex-direction: column;
    background: var(--im-background);
    height: 100%;
    overflow: hidden;

    .chat-list-header {
      height: 50px;
      display: flex;
      align-items: center;
      padding: 0 8px;
      gap: 6px;

      .search-text {
        flex: 1;
        min-width: 0;
      }

      .read-all-btn {
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: var(--im-color-primary);
        font-size: 18px;
        cursor: pointer;
        border-radius: 4px;

        &:hover {
          background: rgba(9, 47, 128, 0.08);
        }

        &.disabled {
          color: #c0c4cc;
          cursor: not-allowed;
          background: transparent;
        }
      }
    }

    .chat-list-loading {
      height: 50px;
      background-color: #eee;

      .el-icon-loading {
        font-size: 24px;
        color: var(--im-text-color-light);
      }

      .el-loading-text {
        color: var(--im-text-color-light);
      }

      .chat-loading-box {
        height: 100%;
      }
    }

    .chat-list-items {
      flex: 1;
      min-height: 0;
    }
  }
}
.el-empty__image {
  width: 120px;
}
.history-chat {
  border-top: 1px solid;
  .history-chat-title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    padding: 10px;
    border-bottom: 1px solid #eee;
    text-align: left;
  }
}
</style>
